import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface User {
  id: number;
  email: string;
  full_name: string | null;
  is_verified: boolean;
  is_admin: boolean;
  tier_id: number | null;
  tier_name: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokens: () => Promise<boolean>;
  setUser: (user: User) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      error: null,

      initialize: async () => {
        const { accessToken, refreshToken } = get();

        if (!accessToken || !refreshToken) {
          set({ isInitialized: true });
          return;
        }

        try {
          // Set token for API calls
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Fetch current user
          const response = await api.get('/auth/me');
          set({
            user: response.data,
            isAuthenticated: true,
            isInitialized: true,
          });
        } catch (error) {
          // Try to refresh tokens
          const refreshed = await get().refreshTokens();
          if (!refreshed) {
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
            });
          }
          set({ isInitialized: true });
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post('/auth/login', { email, password });
          const { access_token, refresh_token } = response.data;

          // Set token for API calls
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          // Fetch user data
          const userResponse = await api.get('/auth/me');

          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            user: userResponse.data,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.detail || 'Login failed',
          });
          throw error;
        }
      },

      register: async (email: string, password: string, fullName?: string) => {
        set({ isLoading: true, error: null });

        try {
          await api.post('/auth/register', {
            email,
            password,
            full_name: fullName,
          });
          set({ isLoading: false });
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.detail || 'Registration failed',
          });
          throw error;
        }
      },

      logout: async () => {
        const { refreshToken } = get();

        try {
          if (refreshToken) {
            await api.post('/auth/logout', { refresh_token: refreshToken });
          }
        } catch (error) {
          // Ignore logout errors
        }

        // Clear auth state
        delete api.defaults.headers.common['Authorization'];
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      refreshTokens: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          return false;
        }

        try {
          const response = await api.post('/auth/refresh', {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token } = response.data;

          // Set new token
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          // Fetch updated user data
          const userResponse = await api.get('/auth/me');

          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            user: userResponse.data,
            isAuthenticated: true,
          });

          return true;
        } catch (error) {
          // Clear invalid tokens to prevent refresh loops
          delete api.defaults.headers.common['Authorization'];
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
          });
          return false;
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
